/* eslint-disable no-bitwise -- Ok for transpilation of code */
import { Float32BufferAttribute, Uint32BufferAttribute } from 'three';
import { Point3D } from '../../../team-apis/otterdam/answers/shape-typings';
import { CloudAttributes } from '../../utils/kd-tree/cloud-attributes.service';
import {
  cloudInstanceId,
  groundInstanceId,
} from '../../utils/kd-tree/segmentation-mask-utils';

interface KDTreePoint {
  position: Point3D;
  instance: 0 | 65535;
}

export function cloudPoint(position: Point3D): KDTreePoint {
  return { position, instance: cloudInstanceId };
}

export function groundPoint(position: Point3D): KDTreePoint {
  return { position, instance: groundInstanceId };
}

export function getPositions(
  pointsWithPosition: Array<{ position: Point3D }>,
): Point3D[] {
  return pointsWithPosition.map((p) => p.position);
}

export function buildKdTreeCloud(kdTreePoints: KDTreePoint[]): CloudAttributes {
  const positions = getPositions(kdTreePoints);
  const [newPositions, mappedIndexes] = kdTreeify(positions);

  const instanceIds = new Uint16Array(kdTreePoints.length);
  mappedIndexes.forEach((oldIndex, newIndex) => {
    instanceIds[newIndex] = kdTreePoints[oldIndex].instance;
  });

  return {
    position: new Float32BufferAttribute(
      new Float32Array(newPositions.flat()),
      3,
    ),
    instanceId: new Uint32BufferAttribute(new Uint32Array(instanceIds), 1),
  };
}

/**
 * Functions below is a transpile of the backend logic to create the SPC KD-Tree
 * structure. We should use this only for tests where we need to define the
 * cloud points.
 * https://github.com/Samasource/giver-pipelines/blob/1d51c8c86f526166f54af054495ae191a2a67393/packages/spc/spc/treeify.py
 */

/**
 * Create a new array with KD-tree ordering
 * Returns a tuple with [new positions, mapped indexes]
 */
function kdTreeify(positions: Point3D[]): [Point3D[], number[]] {
  const indexes = bfsSort(positions);

  const newPositions: Point3D[] = Array(positions.length)
    .fill(null)
    .map(() => [0, 0, 0]);
  const mappedIndexes = Array(positions.length).fill(
    INDEX_WITHOUT_CORRESPONDENCE,
  );

  for (let newIndex = 0; newIndex < indexes.length; newIndex++) {
    const oldIndex = indexes[newIndex];
    newPositions[newIndex][0] = positions[oldIndex][0];
    newPositions[newIndex][1] = positions[oldIndex][1];
    newPositions[newIndex][2] = positions[oldIndex][2];
    mappedIndexes[newIndex] = oldIndex;
  }

  return [newPositions, mappedIndexes];
}

/**
 * Constant for indexes without correspondence
 */
const INDEX_WITHOUT_CORRESPONDENCE = 0xffffffff;

/**
 * Get the next power of 2 of positive integer
 * or return v if it is a power of 2.
 */
function nextPow2(n: number): number {
  let v = n;
  v += v === 0 ? 1 : 0;
  v -= 1;
  v |= v >> 1;
  v |= v >> 2;
  v |= v >> 4;
  v |= v >> 8;
  v |= v >> 16;
  return v + 1;
}

/**
 * Returns the index of the root of a tree of size n.
 * See https://github.com/mikolalysenko/inorder-tree-layout
 */
function root(n: number): number {
  const ptree = (nextPow2(n + 1) >> 1) - 1;
  const f = n - ptree;

  if (nextPow2(f) - 1 >= ptree) {
    return ptree;
  }

  return (ptree >> 1) + f;
}

/**
 * Sort an array of positions to reflect KD-tree structure
 */
function bfsSort(positions: Point3D[]): number[] {
  const result: number[] = [];
  let level: number[][] = [
    Array.from({ length: positions.length }, (_, i) => i),
  ];
  let axis = 0;

  while (level.length > 0) {
    const nextLevel: number[][] = [];

    for (const slice of level) {
      if (slice.length === 0) {
        continue;
      }

      slice.sort((a, b) => positions[a][axis] - positions[b][axis]);
      const median = root(slice.length);
      result.push(slice[median]);

      nextLevel.push(slice.slice(0, median));
      nextLevel.push(slice.slice(median + 1));
    }

    level = nextLevel;
    axis = (axis + 1) % 3;
  }

  return result;
}
